"use client";

import { EVENT_MODE_PUNCHUP, EventCreationForm, STEPS_IDS } from "./types";
import {
  generateNewShow,
  generateNewShowProducers,
} from "../dashboard/venue-manager/shows/utils";

import { Comedian } from "@punchup/data/legacy/comedian";
import { ShowWithComedian } from "@punchup/data/legacy/shows";
import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import { VenuePagesRow } from "@punchup/data/legacy/venue_pages";
import { VenueRow } from "@punchup/data/legacy/venues";
import dayjs from "@punchup/utils/dayjs";
import { toFormPercentageFee } from "@punchup/tixologi/percentage-fee";
import { rgbToHex } from "@punchup/ui-common/punchup/utils/colorMath";
import { defineStepper } from "@stepperize/react";
import { sharedUpdaterFunction } from "@punchup/data/legacy/tour-dates/updaterFunction";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(timezone);
dayjs.extend(utc);

export const getSkippableSteps = (seatChartsEnabled: boolean) => {
  const baseSkippableSteps = [STEPS_IDS.TICKET_TYPES, STEPS_IDS.LINEUP];
  return seatChartsEnabled
    ? [...baseSkippableSteps, STEPS_IDS.SEAT_CHARTS]
    : baseSkippableSteps;
};

export const createStepper = (seatChartsEnabled: boolean) => {
  const showDetailsStep = {
    id: STEPS_IDS.SHOW_DETAILS,
    title: "Show details",
    description: "Enter your show details",
  };
  const ticketTypesStep = {
    id: STEPS_IDS.TICKET_TYPES,
    title: "Create tickets",
    description: "Create your tickets",
  };
  const lineupStep = {
    id: STEPS_IDS.LINEUP,
    title: "Set lineup",
    description: "Select your lineup",
  };
  const seatChartsStep = {
    id: STEPS_IDS.SEAT_CHARTS,
    title: "Seat charts",
    description: "Configure seat charts",
  };
  const reviewStep = {
    id: STEPS_IDS.REVIEW,
    title: "Review details",
    description: "Review your show",
  };

  const steps = seatChartsEnabled
    ? [showDetailsStep, seatChartsStep, ticketTypesStep, lineupStep, reviewStep]
    : [showDetailsStep, ticketTypesStep, lineupStep, reviewStep];

  return defineStepper(...steps);
};

export const { useStepper, steps, utils: stepperUtils } = createStepper(false);

export const doorTimeOptions: { label: string; value: string }[] = [
  // first item is the default
  // empty string means we submit "null", this will hide the door time
  { label: "Do not display door time", value: "" },
  // any other value will display the door time
  { label: "At the show start time", value: "0" },
  { label: "15 minutes before show", value: "-0.25" },
  { label: "30 minutes before show", value: "-0.5" },
  { label: "45 minutes before show", value: "-0.75" },
  { label: "1 hour before show", value: "-1" },
  { label: "2 hours before show", value: "-2" },
  { label: "3 hours before show", value: "-3" },
];

export const doorTimeTooltipContent =
  "Door time is shown with the start time on your site at checkout, on tickets, and in emails.";

export const sellingTimeOptions: { label: string; value: string }[] = [
  { label: "Now", value: "0" },
  { label: "15 minutes before show", value: "-0.25" },
  { label: "30 minutes before show", value: "-0.5" },
  { label: "45 minutes before show", value: "-0.75" },
  { label: "1 hour before show", value: "-1" },
  { label: "2 hours before show", value: "-2" },
  { label: "3 hours before show", value: "-3" },
  { label: "1 day before show", value: "-24" },
  { label: "2 days before show", value: "-48" },
  { label: "3 days before show", value: "-72" },
  { label: "Custom", value: "custom" },
];

export const offSellingTimeOptions: { label: string; value: string }[] = [
  { label: "When show starts", value: "start_date" },
  { label: "When show is over", value: "end_date" },
  { label: "15 minutes before show", value: "-0.25" },
  { label: "30 minutes before show", value: "-0.5" },
  { label: "45 minutes before show", value: "-0.75" },
  { label: "1 hour before show", value: "-1" },
  { label: "2 hours before show", value: "-2" },
  { label: "3 hours before show", value: "-3" },
  { label: "1 day before show", value: "-24" },
  { label: "2 days before show", value: "-48" },
  { label: "3 days before show", value: "-72" },
  { label: "Custom", value: "custom" },
];

export const timezones = [
  {
    label: `Hawaii (${dayjs().tz("Pacific/Honolulu").format("z")})`,
    value: "Pacific/Honolulu",
    time: dayjs().tz("Pacific/Honolulu").format("h:mm A"),
  },
  {
    label: `Alaska (${dayjs().tz("America/Anchorage").format("z")})`,
    value: "America/Anchorage",
    time: dayjs().tz("America/Anchorage").format("h:mm A"),
  },
  {
    label: `Pacific (${dayjs().tz("America/Los_Angeles").format("z")})`,
    value: "America/Los_Angeles",
    time: dayjs().tz("America/Los_Angeles").format("h:mm A"),
  },
  {
    label: `Phoenix (${dayjs().tz("America/Phoenix").format("z")})`,
    value: "America/Phoenix",
    time: dayjs().tz("America/Phoenix").format("h:mm A"),
  },
  {
    label: `Mountain (${dayjs().tz("America/Denver").format("z")})`,
    value: "America/Denver",
    time: dayjs().tz("America/Denver").format("h:mm A"),
  },
  {
    label: `Central (${dayjs().tz("America/Chicago").format("z")})`,
    value: "America/Chicago",
    time: dayjs().tz("America/Chicago").format("h:mm A"),
  },
  {
    label: `Eastern (${dayjs().tz("America/New_York").format("z")})`,
    value: "America/New_York",
    time: dayjs().tz("America/New_York").format("h:mm A"),
  },
];

const yesterday = dayjs().subtract(1, "day");
const startDate = dayjs().utc().add(1, "day");
const endDate = startDate.add(2, "hours");

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const defaultTimeZone =
  timezones.find((tz) => tz.value === userTimezone)?.value ||
  timezones[0].value;

export const defaultValues: EventCreationForm = {
  venue_id: "",
  ticketing_mode: "internal",
  showDetails: {
    mode: EVENT_MODE_PUNCHUP,
    name: "",
    location: "",
    city: "",
    state: "",
    description: "",
    start_date: startDate.format("YYYY-MM-DD"),
    start_time: "19:00",
    end_date: endDate.format("YYYY-MM-DD"),
    end_time: "21:00",
    sale_date: yesterday.toDate(),
    time_zone: defaultTimeZone,
    venue: "",
    support: "",
    postal_code: "",
    doors_open: doorTimeOptions[0].value,
    ticketing_mode: "static",
    quantity: "",
    approval_required: false,
    inactive: false,
    has_multiple_dates: false,
    dates: [],
    image_url: "",
    ticket_delivery_hours_before_event: null,
    banner_image_url: null,
    banner_link_url: null,
  },
  ticketTypes: [
    {
      name: "",
      description: "General Admission",
      initial_price: 0,
      fixed_fee: 0,
      percentage_fee: 0,
      final_price: 0,
      amount: 0,
      selling_date: "0",
      off_selling_date: endDate.format("YYYY-MM-DD"),
      ticket_visibility: true,
      tickets_pool_idx: null,
      color: null,
      color_name: null,
      pay_what_you_want: false,
    },
  ],
  lineup: [],
  seatCharts: {
    selectedChartKey: undefined,
    selectedChartId: undefined,
  },
  pools: [],
};

/**
 * The venue's default ticket fees in the wizard form's units: `fixed_fee` in
 * dollars, `percentage_fee` as a DECIMAL (0.055 for 5.5%) — the form's unit.
 * ticketing-api serves percent; both directions of that conversion live in
 * @punchup/tixologi/percentage-fee, rounded so a value round-trips exactly.
 */
export type WizardTicketFeeDefaults = {
  fixed_fee: number;
  percentage_fee: number;
};

export const toWizardTicketFeeDefaults = (
  served?: {
    configured: boolean;
    fixed_fee: number;
    percentage_fee: number;
  } | null
): WizardTicketFeeDefaults | null =>
  served?.configured
    ? {
        fixed_fee: served.fixed_fee,
        percentage_fee: toFormPercentageFee(served.percentage_fee),
      }
    : null;

/**
 * Seeds every ticket type the wizard proposes — the blank first one and the ones
 * copied from the venue's last event — with the venue default. With no default the
 * form is returned untouched, so a venue without one sees exactly today's wizard.
 * A prefill only: the organiser can still edit each ticket type's fees.
 */
export const applyTicketFeeDefaults = (
  values: EventCreationForm,
  defaults: WizardTicketFeeDefaults | null
): EventCreationForm =>
  defaults
    ? {
        ...values,
        [STEPS_IDS.TICKET_TYPES]: values[STEPS_IDS.TICKET_TYPES].map(
          (ticketType) => ({ ...ticketType, ...defaults })
        ),
      }
    : values;

export const getDefaults = (
  venuePage?: VenuePagesRow,
  venue?: VenueRow,
  lastEvent?: TixologiResumedEvent,
  preSelectedComedianIds?: string[],
  preSelectedDate?: string,
  ticketFeeDefaults?: WizardTicketFeeDefaults | null
): EventCreationForm => {
  let values = defaultValues;

  if (venuePage && venue) {
    values = {
      ...values,
      venue_id: venue.id || "",
      showDetails: {
        ...defaultValues.showDetails,
        // If a venuePage is a producer, use the geolocation name and not the venue page name
        venue: venuePage.is_producer ? venue.name || "" : venuePage.name,
        location: venue.address || "",
        city: venue.city || "",
        state: venue.state || "",
        postal_code: venue.postal_code || "",
      },
    };
  }

  if (lastEvent) {
    const ticketType = lastEvent.ticket_types?.length
      ? lastEvent.ticket_types[0]
      : undefined;

    const startDate = dayjs(lastEvent.start_date).tz(lastEvent.time_zone);
    const endDate = dayjs(lastEvent.end_date).tz(lastEvent.time_zone);
    const eventStartTime = startDate.format("HH:mm");

    const sellingTimeDiff = dayjs(ticketType?.selling_time).diff(
      startDate,
      "hours"
    );
    const offSellingTimeEndDiff = dayjs(ticketType?.off_selling_time).diff(
      endDate,
      "hours"
    );
    const offSellingTimeStartDiff = dayjs(ticketType?.off_selling_time).diff(
      startDate,
      "hours"
    );

    const sellingTimeOption = sellingTimeOptions.find(
      (option) => option.value === sellingTimeDiff.toString()
    );
    const offSellingTimeOption = offSellingTimeOptions.find((option) => {
      if (offSellingTimeEndDiff === 0 && option.value === "end_date") {
        return true;
      } else if (
        offSellingTimeStartDiff === 0 &&
        option.value === "start_date"
      ) {
        return true;
      } else {
        return option.value === offSellingTimeEndDiff.toString();
      }
    });

    const timeZone =
      timezones.find((tz) => tz.value === lastEvent.time_zone)?.value ||
      defaultTimeZone;

    const ticketTypes =
      lastEvent.ticket_types?.map((ticketType) => ({
        name: ticketType.name,
        description: ticketType.description,
        initial_price: ticketType.initial_price,
        fixed_fee: ticketType.fixed_fee || 0,
        // The API serves percent (3 = 3%); the form holds a decimal (0.03).
        percentage_fee: toFormPercentageFee(ticketType.percentage_fee || 0),
        final_price: 0,
        amount: ticketType.amount,
        selling_date: sellingTimeOption?.value || sellingTimeOptions[0].value,
        off_selling_date:
          offSellingTimeOption?.value || offSellingTimeOptions[0].value,
        ticket_visibility: ticketType.ticket_visibility,
        tickets_pool_idx: null,
        bundle_size: ticketType.bundle_size,
        pay_what_you_want: false,
        min_price: null,
        max_price: null,
      })) || [];

    const timeDiff = endDate.diff(startDate, "minutes");
    let newEndDate = dayjs(values.showDetails.start_date).add(
      timeDiff,
      "minutes"
    );
    let newEndTime = newEndDate.format("HH:mm");

    // Ensure end date+time is always after start date+time
    const startDateTime = dayjs(
      values.showDetails.start_date + "T" + eventStartTime
    );
    if (!newEndDate.isAfter(startDateTime)) {
      // Default to 2 hours after start
      newEndDate = startDateTime.add(2, "hours");
      newEndTime = newEndDate.format("HH:mm");
    }

    values = {
      ...values,
      showDetails: {
        ...values.showDetails,
        end_date: newEndDate.format("YYYY-MM-DD"),
        start_time: eventStartTime,
        end_time: newEndTime,
        time_zone: timeZone,
      },
      ticketTypes:
        ticketTypes?.length > 0
          ? ticketTypes
          : [
              {
                name: "",
                description: "",
                initial_price: 0,
                fixed_fee: 0,
                percentage_fee: 0,
                final_price: 0,
                amount: 0,
                selling_date: sellingTimeOptions[0].value,
                off_selling_date: offSellingTimeOptions[0].value,
                ticket_visibility: true,
                tickets_pool_idx: null,
                pay_what_you_want: false,
              },
            ],
      pools: [],
    };
  }

  if (preSelectedDate) {
    const showDate = dayjs(preSelectedDate).format("YYYY-MM-DD");
    const showStartTime = "19:00"; // Default to 7 PM
    const showEndTime = "21:00"; // Default to 9 PM

    values = {
      ...values,
      showDetails: {
        ...values.showDetails,
        start_date: showDate,
        end_date: showDate,
        start_time: showStartTime,
        end_time: showEndTime,
      },

      lineup:
        preSelectedComedianIds && preSelectedComedianIds.length > 0
          ? preSelectedComedianIds
          : [],
    };
  }

  return applyTicketFeeDefaults(values, ticketFeeDefaults ?? null);
};

export const createShow = async (
  event: TixologiResumedEvent,
  selectedComedians: Comedian[],
  venuePage: VenuePagesRow,
  venueForProducers: Pick<VenueRow, "id" | "name" | "city" | "state">
): Promise<ShowWithComedian & { tixologi_event_id: string | null }> => {
  const show = venuePage.is_producer
    ? generateNewShowProducers(
        selectedComedians.length > 0 ? selectedComedians[0] : null,
        [],
        venueForProducers
      )
    : generateNewShow(
        selectedComedians.length > 0 ? selectedComedians[0] : null,
        [],
        venuePage
      );
  const datetime = dayjs
    .tz(event.start_date, event.time_zone)
    .add(dayjs.tz(event.start_date, event.time_zone).utcOffset(), "minutes")
    .format("YYYY-MM-DDTHH:mm");

  const showComedians = selectedComedians.map((comedian, index) => ({
    show_id: show.id,
    comedian_id: comedian.id,
    ordering: index,
    hidden_from_comedian_page: true, // Venue-created shows should be hidden from comedian pages
  }));

  const updatedValues = {
    ...show,
    datetime,
    title: event.name,
    comedian: selectedComedians[0],
    metadata_text: event.description,
    tixologi_event_id: event.id?.toString() || "",
    ticket_link: `${process.env.NEXT_PUBLIC_TIXOLOGI_WIDGET_URL}/event/${event.id}/tickets`,
  } as ShowWithComedian;

  await sharedUpdaterFunction(updatedValues, showComedians, {
    venuePage,
  });

  return updatedValues;
};

export const getPoolColors = (index: number) => {
  const bgColors = [
    "bg-purple-200",
    "bg-green-200",
    "bg-yellow-200",
    "bg-pink-200",
    "bg-blue-200",
    "bg-green-200",
    "bg-yellow-200",
    "bg-orange-200",
    "bg-red-200",
    "bg-gray-200",
  ];
  return bgColors[index % bgColors.length];
};

export const parseTicketPrices = (ticket: {
  initial_price?: number | string | null;
  final_price?: number | string | null;
  fixed_fee?: number | string | null;
  percentage_fee?: number | string | null;
}) => {
  const initialPrice =
    typeof ticket.initial_price === "string"
      ? parseFloat(ticket.initial_price) || 0
      : ticket.initial_price || 0;
  const finalPrice =
    typeof ticket.final_price === "string"
      ? parseFloat(ticket.final_price) || 0
      : ticket.final_price || 0;
  const fixedFee =
    typeof ticket.fixed_fee === "string"
      ? parseFloat(ticket.fixed_fee) || 0
      : ticket.fixed_fee || 0;
  const percentageFee =
    typeof ticket.percentage_fee === "string"
      ? parseFloat(ticket.percentage_fee) || 0
      : ticket.percentage_fee || 0;

  const displayPrice =
    (fixedFee || percentageFee) && finalPrice ? finalPrice : initialPrice;

  return {
    initialPrice,
    finalPrice,
    fixedFee,
    percentageFee,
    displayPrice,
  };
};

/**
 * Seats.io serves category colors as CSS strings (e.g. `rgba(105,6,149,1)`),
 * but a ticket type's color must be a `#RRGGBB` hex. Hex passes through;
 * `rgb()`/`rgba()` drops the alpha; anything else becomes null so a color we
 * can't read never blocks the wizard on a field the manager can't edit.
 */
export const toTicketTypeHexColor = (
  color: string | null | undefined
): string | null => {
  const value = color?.trim();
  if (!value) return null;
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) return value;
  if (/^#[0-9a-fA-F]{8}$/.test(value)) return value.slice(0, 7);
  const rgb = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+%?\s*)?\)$/i
  );
  if (!rgb) return null;
  return rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
};

export const getDateTimeHint = (
  option: string,
  customDatetime: string | null | undefined,
  showStartDate: string,
  showStartTime: string,
  showEndDate: string,
  showEndTime: string,
  timezone: string
): string => {
  if (option === "custom" && customDatetime) {
    return dayjs(customDatetime).tz(timezone).format("MMM D, YYYY [at] h:mm A");
  }
  const showStart = dayjs.tz(`${showStartDate} ${showStartTime}`, timezone);
  const showEnd = dayjs.tz(`${showEndDate} ${showEndTime}`, timezone);

  if (option === "start_date") {
    return showStart.format("MMM D, YYYY [at] h:mm A");
  }

  if (option === "end_date") {
    return showEnd.format("MMM D, YYYY [at] h:mm A");
  }

  if (option === "0") {
    const midnight = dayjs()
      .tz(timezone)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0);
    return midnight.format("MMM D, YYYY [at] h:mm A");
  }

  const hours = parseFloat(option);
  if (!isNaN(hours)) {
    const targetDate = showStart.add(hours, "hours");
    return targetDate.format("MMM D, YYYY [at] h:mm A");
  }

  return "";
};
